// Lo que hace falta para que el visor prestado abra SIN salir a la red —
// ADR-2026-09-08-el-visor-abre-con-la-fila-que-el-panel-ya-tenia — compartido por la
// columna de resultados (`PanelTandaCreaciones`) y el lienzo del Agente
// (ADR-2026-09-16-el-lienzo-abre-el-visor-con-la-fila-que-ya-tiene). Nació en el panel y
// salió acá cuando el lienzo necesitó exactamente lo mismo: la misma fila y la misma
// precarga, no una segunda copia.

import type { FilaInicialVisor } from '../../GeneratedImagesHistory';
import { supabase } from '../../../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from '../../../utils/imageCache';
import type { DatosCelda } from './useDatosDeCeldas';

/**
 * La fila con la que el visor puede abrir en el frame siguiente al toque, o `undefined`
 * si a quien la pide todavía le falta algo: los datos de la card (viajan por lote y
 * pueden no haber llegado) o la firma del original (se pide por adelantado para las
 * celdas visibles; sin ella el visor cae a su camino largo, más lento pero abre igual).
 */
export function filaInicialDeCelda(
  celda: { id: string; src: string; datos: DatosCelda | undefined },
  companyId: string | null,
): FilaInicialVisor | undefined {
  const d = celda.datos;
  if (!companyId || !d?.fileName || !d.createdAt) return undefined;
  const signedUrl = getCachedSignedUrl(`${companyId}/full/${d.fileName}`);
  if (!signedUrl) return undefined;
  return {
    id: celda.id,
    file_name: d.fileName,
    created_at: d.createdAt,
    user_id: d.userId ?? null,
    variante_id: d.varianteId,
    variantes_ids: d.variantesIds,
    datos_generacion: d.datosGeneracion,
    // El mismo criterio que el camino largo del visor: sin producto, "Imagen".
    variante_nombre: d.varianteId ? (d.varianteNombre ?? '') : 'Imagen',
    formato: d.formato ?? '',
    tipo: d.tipo ?? '',
    favorito: d.favorito,
    upscaled_path: d.upscaledPath,
    // La miniatura que la card está dibujando: ya está decodificada en el navegador, así
    // que el visor la muestra en su primer frame y sin red.
    thumbnailUrl: celda.src,
    signedUrl,
  };
}

/**
 * Arranca la descarga de la versión grande (el `media/`, o el original si no lo hay)
 * apenas se toca la card, sin esperar a que el visor monte.
 *
 * El visor hace exactamente esto (`grandePrecargada`) pero recién después de montarse, que
 * son unos cientos de ms de un componente de 7000 líneas. Pedirla acá le gana ese tramo:
 * cuando el visor la pida, ya está en la caché del navegador o a mitad de camino.
 *
 * Sólo imágenes: el `full/` de un video es el mp4 y un `Image()` no lo baja.
 */
export function precargarOriginal(
  fila: Pick<FilaInicialVisor, 'file_name' | 'signedUrl'>,
  companyId: string | null | undefined,
  esVideo: boolean,
): void {
  if (esVideo || !fila.signedUrl) return;
  // Lo que el visor va a mostrar es el `media/` (ver `precargarGrande`), no el original.
  void precargarGrande({ companyId, fileName: fila.file_name, fullUrl: fila.signedUrl });
}

// ── La versión GRANDE que se muestra en el visor ───────────────────────────────────────
//
// El visor de Creaciones (y todo lo que lo reusa: Creaciones recientes, la columna de
// tandas, el lienzo) mostraba el `full/`: el archivo crudo del proveedor, 2-7 MB con 2K
// por defecto. Lo que se MUESTRA ahora es el `media/` (2000 px, JPEG ~0,15 MB, lo escribe
// el worker junto al `opt/`); el `full/` queda para lo que trabaja con el ARCHIVO
// (descargar, compartir, recortar, editar). Las creaciones anteriores al 2026-09-14 no
// tienen `media/`: para esas, el `full/` como antes
// (ADR-2026-10-16-nano-banana-2-1-en-2k-por-defecto; el `media/` nació en
// ADR-2026-09-14-la-resolucion-media, que lo había conectado sólo a los modos V2).

/** Creaciones que ya sabemos que NO tienen `media/`: no se vuelve a pedir la firma. */
const sinMedia = new Set<string>();

/**
 * ¿La firma falló porque el archivo NO EXISTE? Storage responde "Object not found" (HTTP 400
 * con un 404 adentro). Un corte de red o un 5xx no cuentan: recordarlos dejaba la creación
 * en `full/` hasta recargar la página (review atlas-code#187,
 * ADR-2026-10-17-review-187-solo-se-recuerda-el-media-que-no-existe).
 */
export function esArchivoInexistente(error: { message?: string; status?: number } | null | undefined): boolean {
  if (!error) return false;
  return error.status === 404 || /not[\s_-]?found/i.test(error.message ?? '');
}

/** Firma del `media/` de una creación, o null si no lo tiene (o si ahora no se pudo firmar). */
export async function firmarMedia(companyId: string, fileName: string): Promise<string | null> {
  const path = `${companyId}/media/${fileName}`;
  if (sinMedia.has(path)) return null;
  const cacheada = getCachedSignedUrl(path);
  if (cacheada) return cacheada;
  try {
    const { data, error } = await supabase.storage.from('generadas').createSignedUrl(path, 3600);
    if (data?.signedUrl) {
      setCachedSignedUrl(path, data.signedUrl);
      return data.signedUrl;
    }
    // Sólo lo que de verdad no está se recuerda; lo demás cae al full ESTA vez y se reintenta.
    if (esArchivoInexistente(error as { message?: string; status?: number } | null)) sinMedia.add(path);
  } catch {
    /* red caída: cae al full esta vez, sin recordarlo */
  }
  return null;
}

function precargar(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
    // Ya cacheada: `onload` puede no volver a dispararse.
    if (img.complete && img.naturalWidth > 0) resolve(true);
  });
}

/**
 * Baja la versión grande que va a mostrar el visor y devuelve su URL cuando ya está en la
 * caché del navegador: el `media/` si la creación lo tiene, si no (o si no carga) el
 * `full/`. Devuelve null si no bajó ninguna. Los videos no pasan por acá: su `full/` es el
 * mp4 y un `Image()` no lo baja.
 */
export async function precargarGrande(p: {
  companyId: string | null | undefined;
  fileName: string | null | undefined;
  fullUrl: string;
}): Promise<string | null> {
  if (typeof Image === 'undefined' || !p.fullUrl) return null;
  if (p.companyId && p.fileName) {
    const media = await firmarMedia(p.companyId, p.fileName);
    if (media && (await precargar(media))) return media;
  }
  return (await precargar(p.fullUrl)) ? p.fullUrl : null;
}
